import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { setStoredAdminPassword, ADMIN_API_BASE } from "@/lib/adminApi";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

// Reusable login screen used by both AdminDashboard and PlayerProgress.
// Calls /api/admin/login with the password + honeypot + Turnstile token.
// On success: stores the password in localStorage and calls onSuccess(password).
export default function AdminLoginForm({ title = "Admin Access", subtitle, onSuccess }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Render the Turnstile widget. Re-tries until window.turnstile is loaded.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (window.turnstile && containerRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => setTurnstileToken(token),
          "error-callback": () => setTurnstileToken(""),
          "expired-callback": () => setTurnstileToken(""),
        });
      } else if (!window.turnstile) {
        setTimeout(tryRender, 200);
      }
    };
    tryRender();
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetCaptcha = () => {
    if (window.turnstile && widgetIdRef.current !== null) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (e) {}
      setTurnstileToken("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Please complete the captcha");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${ADMIN_API_BASE}/admin/login`, {
        password,
        website,
        turnstile_token: turnstileToken,
      });
      setStoredAdminPassword(password);
      toast.success("Welcome back, Coach!");
      if (onSuccess) onSuccess(password);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid password";
      toast.error(msg);
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-burns-black flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-4">
          <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-32 sm:h-36 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-white">{title}</h1>
        </div>
        {subtitle && <p className="text-zinc-400 mb-6 text-sm text-center">{subtitle}</p>}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          {/* Honeypot — visually hidden, real users never fill it in */}
          <div
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
          >
            <label htmlFor="website-field">Website</label>
            <input
              id="website-field"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white">Password</Label>
            <div className="relative">
              <Input
                id="password"
                name="admin-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoFocus
                className="bg-zinc-800 border-zinc-700 text-white pr-10"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute inset-y-0 right-2 flex items-center text-zinc-500 hover:text-zinc-300"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {TURNSTILE_SITE_KEY && (
            <div ref={containerRef} className="flex justify-center" />
          )}

          <Button
            type="submit"
            disabled={loading || !password || (TURNSTILE_SITE_KEY && !turnstileToken)}
            className="w-full bg-burns-red hover:bg-burns-red/80 text-white"
            data-testid="admin-password-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
            Unlock
          </Button>
        </form>
      </div>
    </div>
  );
}
